// @flow
import React, { Element, useState } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import {merge} from "lodash/object";
import { VARIANT, MODE } from './data';

import { ReactComponent as CopyIcon } from '@assets/icons/pageBlocks/copy.svg';
import { ReactComponent as CrossIcon } from '@assets/icons/pageBlocks/cross.svg';
import {CustomPopover, CustomTooltip, CustomFormControlLabel, CustomSwitch, CustomButton} from './styledComponent';
import styles from './ColorCard.scss';

export type PropTypes = {
  className?: String,
  classes?: {
    root?: String,
    media?: String,
    content?: String,
    title?: String,
    additional?: String,
    description?: String,
  },
  variant?: typeof VARIANT.PRIMARY | typeof VARIANT.SECONDARY,
  mode?: typeof MODE.READ | typeof MODE.EDIT,
  data?: Object,
  onFocus?: Function,
  onBlur?: Function,
  onUpdate?: Function,
  onRemove?: Function,
  lastUpdate?: String,
  collectionCount?: Number
};

const CopyableLabel = ({ className, label, value, disabled = false }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();

    copyValueToClipboard().then(() => {
      setOpen(true);
      setTimeout(() => {
        setOpen(false);
      }, 1000);
    });
  };

  const copyValueToClipboard = () => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(value);
    }
    return Promise.reject('The Clipboard API is not available.');
  };

  return (
    <CustomTooltip open={open} title="Copied" placement="top">
      <div className={styles.CopyableLabelWrapper}>
        <span
          className={classNames(styles.CopyableLabel, styles.CopyableLabelActive, className)}
          onClick={handleClick}
        >
          <span>{label}: {value}</span>
          {!disabled && <CopyIcon />}
        </span>
      </div>
    </CustomTooltip>
  );
};

const hexToRGB = (color) => {
  if (!validHexColor(color)) {
    return null;
  }

  let colorValue = color;
  const matchAlpha = colorValue.match(/^([0-9A-F]{6})([0-9A-F]{2})$/i);

  let r, g, b, a;

  if (matchAlpha) {
    a = Math.round((parseInt(matchAlpha[2], 16) / 255) * 100) / 100;
    colorValue = matchAlpha[1];
  }

  const hasFullSpec = colorValue.length === 6;
  const m = colorValue.match(hasFullSpec ? /(\S{2})/g : /(\S)/g);

  r = parseInt(m[0] + (hasFullSpec ? '' : m[0]), 16);
  g = parseInt(m[1] + (hasFullSpec ? '' : m[1]), 16);
  b = parseInt(m[2] + (hasFullSpec ? '' : m[2]), 16);

  return a ? { r, g, b, a } : { r, g, b };
};

const previewHasBorder = (color) => {
  return !(!!color && validHexColor(color) && !/^(fff|ffffff([0-9A-F]{2})?)$/i.test(color));
  // return !color || brightnessByColor(color) > 240;
};

const validHexColor = (color) => {
  return !!color && /^([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(color);
};

const rgbToHSL = (r, g, b) => {
  r = r / 255;
  g = g / 255;
  b = b / 255;
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
};

export const ColorCard = (
  {
    className,
    classes = {},
    variant = VARIANT.PRIMARY,
    mode = MODE.READ,
    data: initalData,
    onFocus,
    onBlur,
    onUpdate,
    onRemove,
  }: PropTypes): Element<BaseCard> => {

  const [data, setData] = useState({
    title: get(initalData, 'title'),
    description: get(initalData, 'description'),
    hexCode: get(initalData, 'hexCode'),
    cmykCode: get(initalData, 'cmykCode'),
    pantoneCode: get(initalData, 'pantoneCode'),
    highlightCard: get(initalData, 'highlight', false),
  });

  const rgbValues = hexToRGB(data.hexCode);
  const hslValues = rgbValues ? rgbToHSL(rgbValues.r, rgbValues.g, rgbValues.b) : null;
  const validColor = validHexColor(data.hexCode);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleCardClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onFocus();
    setAnchorEl(event.currentTarget);
  };

  const handleEditFormCancel = () => {
    setData({
      title: get(initalData, 'title'),
      description: get(initalData, 'description'),
      hexCode: get(initalData, 'hexCode'),
      cmykCode: get(initalData, 'cmykCode'),
      pantoneCode: get(initalData, 'pantoneCode'),
      highlightCard: get(initalData, 'highlight', false),
    })
    onBlur();
    setAnchorEl(null);
  };

  const handleEditFormSubmit = () => {
    onUpdate(data);
    setAnchorEl(null);
  };

  const openEditForm = Boolean(anchorEl);
  const cardFilled = !!data.title && !!data.hexCode;

  const handleDataChange = (field, value) => {
    setData(p =>({...p, [field]: value}))
  };

  return (
    <div
      className={classNames(
        className,
        classes?.root,
        styles.Root,
        {
          [styles.RootVariantPrimary]: variant === VARIANT.PRIMARY,
          [styles.RootVariantSecondary]: variant === VARIANT.SECONDARY,
          [styles.RootModeRead]: mode === MODE.READ,
          [styles.RootModeEdit]: mode === MODE.EDIT,
          [styles.RootHighlight]: variant === VARIANT.PRIMARY && !openEditForm && data.highlightCard && validColor,
          [styles.RootHighlightDark]: variant === VARIANT.PRIMARY && !openEditForm && data.highlightCard && hslValues?.l < 0.4,
          [styles.RootActive]: openEditForm,
        },
      )}
      style={{backgroundColor: !openEditForm && data.highlightCard && validColor ? `#${data.hexCode}` : '#ffffff'}}
      onClick={mode === MODE.EDIT && !openEditForm ? handleCardClick : undefined}
    >
      {mode === MODE.EDIT && (
        <button
          className={styles.RemoveCardButton}
          onClick={onRemove}
        >
          <CrossIcon />
        </button>
      )}
      <div
        className={classNames(
          styles.Preview,
          {
            [styles.PreviewWithBorder]: previewHasBorder(data.hexCode),
          },
        )}
        style={{ backgroundColor: validHexColor(data.hexCode) ? `#${data.hexCode}` : '#ffffff' }}
      ></div>
      <div className={styles.TitleAndDescription}>
        <div className={styles.Title}>
          {cardFilled ? data.title : 'No colors added yet'}
        </div>
        {mode === MODE.EDIT && !cardFilled && !openEditForm && (
          <div className={styles.EmptyCardSubtitle}>Click to add</div>
        )}
        {variant === VARIANT.PRIMARY && (
          <div className={styles.Description}>
            {data.description}
          </div>
        )}
      </div>
      {cardFilled && (
        <div className={styles.ColorCodes}>
          <CopyableLabel label="HEX" value={data.hexCode} />
          {rgbValues &&
            <CopyableLabel
              label="RGB"
              value={`${rgbValues.r} ${rgbValues.g} ${rgbValues.b}${rgbValues.a ? ` ${rgbValues.a}` : ''}`}
            />}
          {data.cmykCode && <CopyableLabel label="CMYK" value={data.cmykCode} />}
          {data.pantoneCode && <CopyableLabel label="PANTONE" value={data.pantoneCode} />}
        </div>
      )}
      <CustomPopover
        anchorEl={anchorEl}
        open={openEditForm}
        onClose={() => handleEditFormCancel()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: -32,
        }}
        disableScrollLock
      >
        <div className={styles.ColorFormPopover}>
          <button
            className={styles.ClosePopoverButton}
            onClick={() => handleEditFormCancel()}
          >
            <CrossIcon />
          </button>
          <div className={styles.ColorFormTitle}>
            Edit {variant === VARIANT.PRIMARY ? 'primary' : 'secondary'} color
          </div>
          <div>
            <div className={styles.InputFields}>
              {variant === VARIANT.PRIMARY && (
                <CustomFormControlLabel
                  control={
                    <CustomSwitch
                      disableRipple
                      checked={!!data.highlight}
                      onChange={(e) => handleDataChange('highlight', !data.highlight)}
                    />
                  }
                  label="Highlight Card"
                />
              )}
              <input
                type="text"
                placeholder="Color title *"
                value={data.title}
                onChange={(e) => handleDataChange( 'title', e.target.value)}
              />
              {variant === VARIANT.PRIMARY && (
                <input
                  type="text"
                  placeholder="Color description"
                  value={data.description}
                  onChange={(e) => handleDataChange("description", e.target.value)}
                />
              )}
              <input
                type="text"
                placeholder="HEX *"
                value={data.hexCode}
                onChange={(e) => handleDataChange('hexCode', e.target.value)}
              />
              <input
                type="text"
                placeholder="CMYK"
                value={data.cmykCode}
                onChange={(e) => handleDataChange('cmykCode', e.target.value)}
              />
              <input
                type="text"
                placeholder="Pantone"
                value={data.pantoneCode}
                onChange={(e) => handleDataChange('pantoneCode', e.target.value)}
              />
            </div>
            <div className={styles.FormButtons}>
              <CustomButton variant="outlined" onClick={(e) => {
                handleEditFormCancel(e);
              }}>Cancel</CustomButton>
              <CustomButton
                variant="contained"
                disabled={!data.title || !data.hexCode}
                onClick={(e) => {
                  handleEditFormSubmit(e);
                }}
              >Save</CustomButton>
            </div>
          </div>
        </div>
      </CustomPopover>
    </div>
  );
};

export default ColorCard;
